import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button as MuiButton,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import PageHeader1 from '../../components/common/PageHeader1';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { usePermissions } from '../../context/AuthContext';

function EductionCoupon() {
    const { can } = usePermissions();
    const [coupons, setCoupons] = useState([]);
    const [tabValue, setTabValue] = useState(0);
    const [open, setOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [filterModel, setFilterModel] = useState({
        items: [],
        quickFilterExcludeHiddenColumns: true,
        quickFilterValues: [''],
    });

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [pagination, setPagination] = useState({
        totalDocs: 0,
        totalPages: 0,
    });
    const [loading, setLoading] = useState(false);

    const categoryMap = ['flat_off_rupees', 'flat_off_percentage'];

    const fetchCoupons = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("vedhvidhi-admin-token");
            if (!token) throw new Error("Auth token not found");

            const category = categoryMap[tabValue];
            const { page, pageSize } = paginationModel;

            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/admin/coupon?page=${page + 1}&limit=${pageSize}&category=${category}&type=education`,
                { headers: { Authorization: token } }
            );

            const data = response.data;
            setCoupons(data.data || []);
            setPagination(data.paginationDetail || { totalDocs: 0, totalPages: 0 });
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch coupons.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCoupons();
    }, [paginationModel, tabValue]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    const handleClickOpen = (userId) => {
        setSelectedUserId(userId);
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
        setSelectedUserId(null);
    };

    const handleDelete = async () => {
        if (!selectedUserId) return;
        setLoading(true);
        try {
            const token = localStorage.getItem("vedhvidhi-admin-token");
            await axios.delete(`${process.env.REACT_APP_BASEURL}/admin/coupon/${selectedUserId}`, {
                headers: { Authorization: token }
            });
            toast.success("Coupon deleted successfully!");

            const totalDocs = pagination.totalDocs - 1;
            const totalPages = Math.ceil(totalDocs / paginationModel.pageSize);
            const newPage = Math.max(0, Math.min(paginationModel.page, totalPages - 1));
            setPaginationModel(prev => ({ ...prev, page: newPage }));

            fetchCoupons();
        } catch (err) {
            toast.error("Failed to delete coupon.");
        } finally {
            handleClose();
            setLoading(false);
        }
    };

    const formatDateOnly = (dateString) => {
        if (!dateString) return "N/A";
        // Extract date part (YYYY-MM-DD) from ISO string
        const datePart = dateString.split('T')[0];
        const [year, month, day] = datePart.split('-');
        return `${day}/${month}/${year}`;
    };

    const rows = coupons.map((item, index) => ({
        id: item._id,
        sNo: paginationModel.page * paginationModel.pageSize + index + 1,
        couponCode: item.couponCode,
        category: item.category,
        discountValue: item.discountValue,
        validFrom: formatDateOnly(item.validFrom),
        validTo: formatDateOnly(item.validTo),
        minCartValue: item.minCartValue,
        createdAt: item.createdAt ? new Date(item.createdAt).toLocaleString() : "",
    }));

    const columns = [
        { field: 'sNo', headerName: 'S.No', width: 80 },
        { field: 'couponCode', headerName: 'Coupon Code', width: 150 },
        { field: 'category', headerName: 'Category', width: 150 },
        { field: 'discountValue', headerName: 'Discount', width: 150 },
        { field: 'validFrom', headerName: 'Start Date', width: 150 },
        { field: 'validTo', headerName: 'End Date', width: 150 },
        { field: 'minCartValue', headerName: 'Minimum Cart Value', width: 150 },
        { field: 'createdAt', headerName: 'Created At', width: 200 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: ({ row }) => (
                <>
                    {can('education_coupons:delete') && (
                        <Tooltip title="Delete Coupon">
                            <IconButton onClick={() => handleClickOpen(row.id)}>
                                <i className="icofont-ui-delete text-danger"></i>
                            </IconButton>
                        </Tooltip>
                    )}
                </>
            ),
        },
    ];

    return (
        <div className="body d-flex py-1">
            <Toaster />
            <div className="container-xxl">
                <PageHeader1
                    pagetitle='Education Coupon List'
                    righttitle='Add Coupon'
                    link='/educationcoupon-add'
                    routebutton={can('education_coupons:add')}
                />

                <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label="Flat Rs Discount" />
                        <Tab label="Percent Discount" />
                    </Tabs>
                </Box>

                <div className="row g-0 mb-3">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-body">
                                <Box sx={{ height: 400 }}>
                                    <DataGrid
                                        loading={loading}
                                        columns={columns}
                                        rows={rows}
                                        rowCount={pagination.totalDocs || 0}
                                        paginationMode="server"
                                        paginationModel={paginationModel}
                                        onPaginationModelChange={setPaginationModel}
                                        disableColumnFilter
                                        disableDensitySelector
                                        slots={{ toolbar: GridToolbar }}
                                        disableRowSelectionOnClick
                                        pageSizeOptions={[10, 25, 50, 100]}
                                        filterModel={filterModel}
                                        onFilterModelChange={(newModel) => setFilterModel(newModel)}
                                        columnVisibilityModel={columnVisibilityModel}
                                        onColumnVisibilityModelChange={setColumnVisibilityModel}
                                        autoHeight
                                        getRowId={(row) => row.id}
                                    />
                                </Box>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Delete Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to delete this coupon?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={handleClose}>Cancel</MuiButton>
                    <MuiButton onClick={handleDelete} color="error" disabled={loading}>
                        {loading ? <CircularProgress size={20} /> : "Delete"}
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default EductionCoupon;